import { Trans, useLingui } from '@lingui/react';
import { Box } from '@startuptools/ui/Box';
import { Button } from '@startuptools/ui/Button';
import { ButtonGroup } from '@startuptools/ui/ButtonGroup';
import { Card } from '@startuptools/ui/Card';
import { Checkbox } from '@startuptools/ui/Checkbox';
import { DateFormat } from '@startuptools/ui/DateFormat';
import DeleteIcon from '@mui/icons-material/Delete';
import { Divider } from '@startuptools/ui/Divider';
import EditIcon from '@mui/icons-material/Edit';
import { IconButton } from '@startuptools/ui/IconButton';
import { List } from '@startuptools/ui/List';
import { ListItem } from '@startuptools/ui/ListItem';
import { ListItemDecorator } from '@startuptools/ui/ListItemDecorator';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import {
  useCreateMeetingTodoMutation,
  useDeleteMeetingTodoMutation,
  useMtSetDoneMutation,
  useUpdateTodoMutation,
} from '../../../graphql/react-operations';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { useState } from 'react';
import { AddTodoDialog } from './AddTodoDialog';
import { TodoShowDocument, useTodoShowSuspenseQuery } from './TodoShow.graphql';
import { type SubmitHandler } from 'react-hook-form';
import { type EditableTodo, type FormValues } from './TodoForm';

export const MeetingTodoShow = ({ meetingId }: { meetingId: string }) => {
  const companyId = useCompanyId();
  const { i18n } = useLingui();
  const [showAddTodo, setShowAddTodo] = useState<EditableTodo | true | null>(null);
  const {
    data: { meetingTodos },
  } = useTodoShowSuspenseQuery({ variables: { companyId, meetingId } });

  // eslint-disable-next-line sut/loading-mutation
  const [doSetDone] = useMtSetDoneMutation({ refetchQueries: [TodoShowDocument] });
  // eslint-disable-next-line sut/loading-mutation
  const [doDelete] = useDeleteMeetingTodoMutation({ refetchQueries: [TodoShowDocument] });
  // eslint-disable-next-line sut/loading-mutation
  const [doCreate] = useCreateMeetingTodoMutation({ refetchQueries: [TodoShowDocument] });
  // eslint-disable-next-line sut/loading-mutation
  const [doEdit] = useUpdateTodoMutation({ refetchQueries: [TodoShowDocument] });

  const onSubmit: SubmitHandler<FormValues> = async ({ identity, id, dueDate: dueDateDt, ...rest }) => {
    const dueDate = dueDateDt?.toUTC(0, { keepLocalTime: true }).toISO() ?? null;
    if (id) {
      await doEdit({ variables: { companyId, input: { dueDate, identityId: identity?.id, id, ...rest } } });
    } else {
      await doCreate({
        variables: {
          companyId,
          input: {
            meetingId,
            todoCreate: {
              dueDate,
              identityId: identity?.id,
              ...rest,
            },
          },
        },
      });
    }
    setShowAddTodo(null);
  };

  return (
    <>
      <Card>
        <Stack gap={3}>
          <Box>
            <Button onClick={() => setShowAddTodo(true)}>
              <Trans id="Add Todo" />
            </Button>
          </Box>
          <List>
            {meetingTodos.map(mt => (
              <ListItem
                key={mt.id}
                endAction={
                  <ButtonGroup>
                    <IconButton color="primary" onClick={() => setShowAddTodo(mt.todo)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton color="danger" onClick={() => doDelete({ variables: { companyId, id: mt.id } })}>
                      <DeleteIcon />
                    </IconButton>
                  </ButtonGroup>
                }
              >
                <ListItemDecorator>
                  <Checkbox
                    checked={mt.todo.done}
                    onChange={v => {
                      void doSetDone({
                        variables: {
                          companyId,
                          input: { id: mt.id, todoDone: { id: mt.todo.id, done: v.target.checked } },
                        },
                      });
                    }}
                  />
                </ListItemDecorator>
                <Stack m={1}>
                  <Typography>{mt.todo.descriptionPrimaryLang}</Typography>
                  <Stack direction="row" gap={1}>
                    {mt.todo.dueDate && (
                      <Typography level="body-sm">
                        <Trans
                          id="Due Date: <dateFormat>{date}</dateFormat>"
                          values={{ date: mt.todo.dueDate }}
                          components={{ dateFormat: <DateFormat locale={i18n.locale} /> }}
                        />
                      </Typography>
                    )}
                    {mt.todo.identity && (
                      <>
                        {mt.todo.dueDate && <Divider orientation="vertical" />}
                        <Typography level="body-sm">
                          <Trans
                            id="Assignee: {name}"
                            values={{
                              name: mt.todo.identity.name,
                            }}
                          />
                        </Typography>
                      </>
                    )}
                  </Stack>
                </Stack>
              </ListItem>
            ))}
          </List>
        </Stack>
      </Card>
      {showAddTodo && (
        <AddTodoDialog
          onClose={() => setShowAddTodo(null)}
          refetchQueries={[TodoShowDocument]}
          onSubmit={onSubmit}
          todo={showAddTodo !== true ? showAddTodo : undefined}
        />
      )}
    </>
  );
};
